// Phase 6 commands: customer feedback, tickets, rewards (04 §4.8, 15 §15.2–15.3).
//
// The customer ones are written by the server on behalf of `customer:{uid}` (the customer callables
// and the WhatsApp assistant); their staff `permission` is the key a member of staff would need to
// do the same work (`customers.care`), and each handler refuses a staff sender where the work is only
// ever a customer's own. The staff ones come from the office.
import { z } from 'zod';
import { defineCommand } from '../define';
import { trackingNoSchema, customerIdSchema } from '../cargo';
import { pickupWindowSchema } from '../finalmile';
import { RESOLUTION_ACTIONS, ratingSchema, ticketCategorySchema, RATING_KINDS } from '../customer';

const ulidId = (prefix: string) =>
  z.string().regex(new RegExp(`^${prefix}_[0-9A-HJKMNP-TV-Z]{26}$`));

// ---- Customer ---------------------------------------------------------------------------------

export const openTicket = defineCommand({
  type: 'openTicket',
  permission: 'customers.care',
  principals: ['customer'],
  offline: false,
  payload: z
    .object({
      ticketId: ulidId('tkt'),
      category: ticketCategorySchema,
      subject: z.string().trim().min(3).max(200),
      message: z.string().trim().min(3).max(2000),
      trackingNo: trackingNoSchema.optional(),
      source: z.enum(['app', 'whatsapp', 'website']).default('app'),
    })
    .strict(),
});

export const replyTicket = defineCommand({
  type: 'replyTicket',
  permission: 'customers.care',
  principals: ['customer'],
  offline: false,
  payload: z
    .object({
      ticketId: ulidId('tkt'),
      message: z.string().trim().min(1).max(2000),
      source: z.enum(['app', 'whatsapp', 'website']).default('app'),
      /** The answer to "was it solved?": yes closes the case, no brings it back. */
      solved: z.boolean().optional(),
    })
    .strict(),
});

/** A rating from the app or from a WhatsApp reply. 1–3 or a complaint opens a ticket. */
export const submitFeedback = defineCommand({
  type: 'submitFeedback',
  permission: 'customers.care',
  principals: ['customer'],
  offline: false,
  payload: z
    .object({
      feedbackId: ulidId('fbk'),
      /** The ticket id to use if one has to be opened, so a retry opens the same one. */
      ticketId: ulidId('tkt'),
      trackingNo: trackingNoSchema,
      stars: ratingSchema,
      comment: z.string().trim().max(1000).default(''),
      kind: z.enum(RATING_KINDS).optional(),
      photos: z.array(z.string().max(200)).max(5).default([]),
      publishConsent: z.boolean().default(false),
      source: z.enum(['app', 'whatsapp']).default('app'),
    })
    .strict(),
});

/** The customer moves a pickup that has not started. Staff with `pickup.manage` may too. */
export const reschedulePickup = defineCommand({
  type: 'reschedulePickup',
  permission: 'pickup.manage',
  principals: ['customer'],
  offline: false,
  payload: z
    .object({ pickupRequestId: z.string().min(1).max(40), window: pickupWindowSchema })
    .strict(),
});

/** Points into a voucher from the rules' catalogue (15 §15.2). */
export const redeemVoucher = defineCommand({
  type: 'redeemVoucher',
  permission: 'rewards.adjust',
  principals: ['customer'],
  offline: false,
  payload: z
    .object({
      catalogId: z.string().regex(/^[a-z0-9]+(-[a-z0-9]+)*$/),
      /** Fixed by the caller so a retry returns the same voucher: 10 capital letters or digits. */
      voucherCode: z.string().regex(/^[A-Z0-9]{10}$/),
    })
    .strict(),
});

// ---- Staff ------------------------------------------------------------------------------------

export const assignTicket = defineCommand({
  type: 'assignTicket',
  permission: 'customers.care',
  principals: [],
  offline: false,
  payload: z.object({ ticketId: ulidId('tkt'), assigneeUid: z.string().min(1).max(128) }).strict(),
});

/** An internal note, or (with `visibleToCustomer`) a reply the customer is sent. */
export const addTicketNote = defineCommand({
  type: 'addTicketNote',
  permission: 'customers.care',
  principals: [],
  offline: false,
  payload: z
    .object({
      ticketId: ulidId('tkt'),
      text: z.string().trim().min(1).max(2000),
      visibleToCustomer: z.boolean().default(false),
    })
    .strict(),
});

export const resolveTicket = defineCommand({
  type: 'resolveTicket',
  permission: 'customers.care',
  principals: [],
  offline: false,
  payload: z
    .object({
      ticketId: ulidId('tkt'),
      action: z.enum(RESOLUTION_ACTIONS),
      /** What the customer is told. */
      summary: z.string().trim().min(3).max(1000),
      /** For `voucher`: a gesture of goodwill, an amount or a percentage. */
      voucher: z
        .object({
          label: z.string().trim().min(1).max(80),
          valueMinor: z.number().int().positive().max(1e9).optional(),
          percent: z.number().min(1).max(100).optional(),
          validDays: z.number().int().positive().max(365).default(90),
          /** Fixed by the caller so a retry returns the same voucher. */
          code: z.string().regex(/^[A-Z0-9]{10}$/),
        })
        .strict()
        .refine((v) => (v.valueMinor !== undefined) !== (v.percent !== undefined), {
          message: 'A voucher is an amount or a percentage',
        })
        .optional(),
      /** For `partner_warning`. */
      partnerId: z.string().max(40).optional(),
      /** For `claim`: the claim reference or a short description. */
      claimNote: z.string().max(300).optional(),
      internalNote: z.string().max(2000).optional(),
    })
    .strict(),
});

/** Points added or taken by a person, with a reason (07: `rewards.adjust`). */
export const adjustRewards = defineCommand({
  type: 'adjustRewards',
  permission: 'rewards.adjust',
  principals: [],
  offline: false,
  payload: z
    .object({
      customerId: customerIdSchema,
      /** Positive adds confirmed points, negative takes them (never below zero). */
      points: z
        .number()
        .int()
        .refine((n) => n !== 0, 'Adjust by at least one point')
        .refine((n) => Math.abs(n) <= 100_000, 'At most 100,000 points at a time'),
      reason: z.string().trim().min(5).max(300),
    })
    .strict(),
});

export const CUSTOMER_COMMANDS = {
  openTicket,
  replyTicket,
  submitFeedback,
  reschedulePickup,
  redeemVoucher,
  assignTicket,
  addTicketNote,
  resolveTicket,
  adjustRewards,
} as const;
